/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, defer, timer, of } from 'rxjs';
import { retry, catchError, takeUntil, delay, tap } from 'rxjs/operators';
import { SwaggerStateService } from './swagger-state.service';
import { SwaggerRequest } from './models/swagger-state.model';
import { CurlPart } from './models/curl-command.model';

/**
 * Swagger DOM Manipulation Service
 *
 * Handles all DOM operations for Swagger UI integration including:
 * - Finding operation containers in the DOM
 * - Injecting curl commands into UI elements
 * - Managing URL displays
 * - Syntax highlighting for curl commands
 *
 * This service replaces the SwaggerUICurlInjector object from apim-proxy.config.ts
 */
@Injectable({
  providedIn: 'root'
})
export class SwaggerDomService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly stateService: SwaggerStateService) {}

  // ============================================================================
  // Public API Methods
  // ============================================================================

  /**
   * Injects data-url attributes to operation path elements
   * @param domNode Root Swagger UI DOM node
   * @param baseUrl Base server URL to use for constructing full URLs
   * @returns Observable that completes when injection is done
   */
  injectDataUrlAttributes(domNode: Element, baseUrl: string): Observable<void> {
    return defer(() => {
      const pathElements = domNode.querySelectorAll('.opblock-summary-path');

      if (pathElements.length === 0) {
        throw new Error('No path elements found');
      }

      pathElements.forEach((pathElement: Element) => {
        const pathText = pathElement.textContent?.trim();
        if (pathText) {
          const fullUrl = baseUrl.endsWith('/')
            ? baseUrl + pathText.substring(1)
            : baseUrl + pathText;

          pathElement.setAttribute('data-url', fullUrl);
        }
      });

      console.log(`✅ Injected data-url attributes to ${pathElements.length} path elements`);
      return of(void 0);
    }).pipe(
      retry({
        count: 5,
        delay: 500
      }),
      catchError(error => {
        console.warn('⚠️ Failed to inject data-url attributes after retries:', error);
        return of(void 0);
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Updates data-url attributes when server selection changes
   * @param domNode Root Swagger UI DOM node
   * @param newServerUrl New base server URL
   */
  updateDataUrlAttributesForServer(domNode: Element, newServerUrl: string): void {
    const pathElements = domNode.querySelectorAll('.opblock-summary-path[data-url]');

    pathElements.forEach((pathElement: Element) => {
      const pathText = pathElement.textContent?.trim();
      if (pathText) {
        const newFullUrl = newServerUrl.endsWith('/')
          ? newServerUrl + pathText.substring(1)
          : newServerUrl + pathText;

        pathElement.setAttribute('data-url', newFullUrl);
      }
    });

    console.log(`✅ Updated ${pathElements.length} data-url attributes for server: ${newServerUrl}`);
  }

  /**
   * Sets up server selection change handler
   * @param domNode Root Swagger UI DOM node
   * @param onServerChange Callback when server changes
   * @returns Observable that monitors for server select element
   */
  setupServerSelectionHandler(
    domNode: Element,
    onServerChange: (serverUrl: string) => void
  ): Observable<void> {
    return timer(0, 500).pipe(
      tap(() => {
        const serverSelect = domNode.querySelector('.servers select') as HTMLSelectElement;
        if (serverSelect && !serverSelect.hasAttribute('data-listener-attached')) {
          serverSelect.setAttribute('data-listener-attached', 'true');

          serverSelect.addEventListener('change', (event) => {
            const target = event.target as HTMLSelectElement;
            onServerChange(target.value);
          });

          console.log('✅ Server selection handler attached');
        }
      }),
      catchError(error => {
        console.error('Error setting up server selection handler:', error);
        return of(void 0);
      }),
      takeUntil(this.destroy$)
    ) as Observable<void>;
  }

  /**
   * Inject display request information into Swagger UI
   * @param displayRequest Display-friendly request data for UI presentation
   * @param proxyUrl Proxied URL
   */
  injectOriginalRequestInfo(displayRequest: SwaggerRequest, proxyUrl: string): void {
    console.log('💉 Injecting display request info for specific operation:', displayRequest.url);

    // Populate curl sections for the SPECIFIC operation that made this request
    this.populateCurlForSpecificOperation(displayRequest, proxyUrl);
  }

  /**
   * Inject curl command with retry logic
   * @param request Display request to inject
   * @param maxAttempts Maximum number of retry attempts
   * @returns Observable that emits true on success, false on failure
   */
  injectCurlWithRetry(
    request: SwaggerRequest,
    proxyUrl: string,
    maxAttempts: number = 5
  ): Observable<boolean> {
    return defer(() => this.attemptCurlInjection(request, proxyUrl)).pipe(
      retry({
        count: maxAttempts,
        delay: (error, retryCount) => {
          const delayMs = retryCount * 100;
          console.log(`⏳ Retry attempt ${retryCount} after ${delayMs}ms`);
          return timer(delayMs);
        }
      }),
      catchError((error) => {
        console.error('❌ Failed to inject curl after retries:', error);
        return of(false);
      }),
      takeUntil(this.destroy$)
    );
  }

  // ============================================================================
  // Operation Finding Methods
  // ============================================================================

  /**
   * Find the specific operation container that matches the request
   * @param displayRequest Display request to find operation for
   * @returns Element or null if not found
   */
  findOperationByRequest(displayRequest: SwaggerRequest): Element | null {
    try {
      const requestUrl = new URL(displayRequest.url);
      const requestMethod = displayRequest.method?.toUpperCase() || 'GET';

      // Find all expanded operation blocks
      const expandedOperations = document.querySelectorAll('.opblock.is-open');

      for (const operation of expandedOperations) {
        if (this.isOperationMatch(operation, requestMethod, displayRequest.url)) {
          return operation;
        }
      }

      return null;

    } catch (error) {
      console.error('❌ Error finding operation by request:', error);
      return null;
    }
  }

  /**
   * Check if an operation element matches the request method and URL
   */
  private isOperationMatch(
    operation: Element,
    requestMethod: string,
    requestUrl: string
  ): boolean {
    try {
      // Get method from operation
      const methodElement = operation.querySelector('.opblock-summary-method');
      const operationMethod = methodElement?.textContent?.trim().toUpperCase();

      // Get path element to check for data-url attribute
      const pathElement = operation.querySelector('.opblock-summary-path');
      const operationPath = pathElement?.textContent?.trim();
      const operationUrl = pathElement?.getAttribute('data-url');

      if (!operationMethod || !operationPath) {
        return false;
      }

      // Check method match
      if (operationMethod !== requestMethod) {
        return false;
      }

      // If we have data-url attribute, use full URL matching
      if (operationUrl) {
        return this.urlsMatch(operationUrl, requestUrl);
      } else {
        // Fallback to path matching
        const requestPath = new URL(requestUrl).pathname;
        return this.pathsMatch(operationPath, requestPath);
      }

    } catch (error) {
      console.debug('Error checking operation match:', error);
      return false;
    }
  }

  /**
   * Check if two paths match, accounting for path parameters
   */
  private pathsMatch(operationPath: string, requestPath: string): boolean {
    // Direct match
    if (operationPath === requestPath) {
      return true;
    }

    // Handle path parameters like /users/{id} matching /users/123
    const operationSegments = operationPath.split('/').reverse();
    const requestSegments = requestPath.split('/').reverse();

    if (operationSegments.length !== requestSegments.length) {
      return false;
    }

    for (let i = 0; i < operationSegments.length; i++) {
      const operationSegment = operationSegments[i];
      const requestSegment = requestSegments[i];

      // If operation segment is a parameter (contains { }), it matches any value
      if (operationSegment.includes('{') && operationSegment.includes('}')) {
        continue;
      }

      // Otherwise, segments must match exactly
      if (operationSegment !== requestSegment) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if two full URLs match, accounting for path parameters
   */
  private urlsMatch(operationUrl: string, requestUrl: string): boolean {
    try {
      // Direct match
      if (operationUrl === requestUrl) {
        return true;
      }

      // Parse URLs to compare components
      const operationUrlObj = new URL(operationUrl);
      const requestUrlObj = new URL(requestUrl);

      // Check if hosts match
      if (operationUrlObj.host !== requestUrlObj.host) {
        return false;
      }

      // Use path matching for the pathname portion
      return this.pathsMatch(operationUrlObj.pathname, requestUrlObj.pathname);

    } catch (error) {
      console.warn('Error parsing URLs for matching:', error);
      return operationUrl === requestUrl;
    }
  }

  // ============================================================================
  // Curl Population Methods
  // ============================================================================

  /**
   * Populate curl sections for the specific operation that made the request
   */
  private populateCurlForSpecificOperation(
    displayRequest: SwaggerRequest,
    proxyUrl: string
  ): void {
    // Find the operation container that matches this request
    const operationContainer = this.findOperationByRequest(displayRequest);

    if (!operationContainer) {
      // Fallback to global update
      this.populateCurlFromDisplayRequest(displayRequest, proxyUrl);
      return;
    }

    // Build curl command directly from display request data
    const curlParts = this.buildCurlFromRequestData(displayRequest);

    // Update only the curl elements within this specific operation
    this.populateCurlElementsInContainer(curlParts, displayRequest.url, operationContainer);

    // Also set the request URL displays within this operation
    this.setRequestUrlInContainer(displayRequest.url, proxyUrl, operationContainer);
  }

  /**
   * Populate curl sections from display request (fallback)
   */
  private populateCurlFromDisplayRequest(
    displayRequest: SwaggerRequest,
    proxyUrl: string
  ): void {
    const curlParts = this.buildCurlFromRequestData(displayRequest);
    this.populateAllCurlElements(curlParts, displayRequest.url);
  }

  /**
   * Build curl command parts from request data
   */
  buildCurlFromRequestData(request: SwaggerRequest): CurlPart[] {
    const method = request.method?.toUpperCase() || 'GET';
    const url = request.url;
    const headers = request.headers || {};
    const body = request.body;

    const curlParts: CurlPart[] = [];

    const addCommand = (type: 'method' | 'url' | 'header' | 'data', content: string) => {
      curlParts.push({ type, content, color: 'lightgreen' });
    };
    const addText = (content: string) => {
      curlParts.push({ type: 'text', content, color: 'white' });
    };
    const addLinebreak = () => {
      curlParts.push({ type: 'linebreak', content: ' \\\n  ' });
    };

    // Main command with method
    addText('curl -X ');
    addCommand('method', `'${method}'`);

    // Add URL
    addLinebreak();
    addCommand('url', `'${url}'`);

    // Add all headers
    Object.entries(headers).forEach(([key, value]) => {
      addLinebreak();
      addText('-H ');
      addCommand('header', `'${key}: ${String(value)}'`);
    });

    // Add body if present
    if (body) {
      const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
      addLinebreak();
      addText('-d ');
      addCommand('data', `'${bodyStr}'`);
    }

    return curlParts;
  }

  /**
   * Populate ALL curl elements in the document
   */
  private populateAllCurlElements(curlParts: CurlPart[], originalUrl: string): void {
    const plainTextCurl = this.curlPartsToPlainText(curlParts);
    const htmlCurl = this.curlPartsToHighlightedHtml(curlParts);

    // Update textareas
    const curlTextareas = document.querySelectorAll('.curl textarea, [class*="curl"] textarea');
    curlTextareas.forEach(element => {
      const textarea = element as HTMLTextAreaElement;
      if (this.shouldUpdateElement(textarea.value)) {
        textarea.value = plainTextCurl;
        console.log('💉 Set plain text curl in curl section textarea');
      }
    });

    // Update microlight elements
    const curlMicrolightElements = document.querySelectorAll('.curl .microlight, [class*="curl"] .microlight');
    curlMicrolightElements.forEach(element => {
      element.innerHTML = htmlCurl;
      this.applyMicrolightStyling(element as HTMLElement);
      console.log('🎨 Set highlighted curl in curl section microlight element');
    });

    // Update code elements
    const curlCodeElements = document.querySelectorAll(`
      .curl pre:not(.microlight),
      .curl code:not(.microlight),
      [class*="curl"] pre:not(.microlight),
      [class*="curl"] code:not(.microlight)
    `.replace(/\s+/g, ' '));

    curlCodeElements.forEach(element => {
      const currentContent = element.textContent || '';
      if (this.shouldUpdateElement(currentContent)) {
        element.textContent = plainTextCurl;
        console.log('💉 Set plain text curl in curl section code element');
      }
    });

    console.log(`💉 Curl-only update summary:
      - Curl textareas: ${curlTextareas.length}
      - Curl microlight elements: ${curlMicrolightElements.length}
      - Curl code elements: ${curlCodeElements.length}`);
  }

  /**
   * Populate curl elements within a specific container
   */
  private populateCurlElementsInContainer(
    curlParts: CurlPart[],
    originalUrl: string,
    container: Element
  ): void {
    console.log('🎯 Populating curl elements within specific operation container for:', originalUrl);

    const plainTextCurl = this.curlPartsToPlainText(curlParts);
    const htmlCurl = this.curlPartsToHighlightedHtml(curlParts);

    // Update textareas within container
    const curlTextareas = container.querySelectorAll('.curl textarea, [class*="curl"] textarea');
    curlTextareas.forEach((element: Element) => {
      const textarea = element as HTMLTextAreaElement;
      if (this.shouldUpdateElement(textarea.value)) {
        textarea.value = plainTextCurl;
        console.log('🎯 Set plain text curl in specific operation textarea');
      }
    });

    // Update microlight elements within container
    const curlMicrolightElements = container.querySelectorAll('.curl .microlight, [class*="curl"] .microlight');
    curlMicrolightElements.forEach((element: Element) => {
      element.innerHTML = htmlCurl;
      this.applyMicrolightStyling(element as HTMLElement);
      console.log('🎯 Set highlighted curl in specific operation microlight element');
    });

    // Update code elements within container
    const curlCodeElements = container.querySelectorAll(`
      .curl pre:not(.microlight),
      .curl code:not(.microlight),
      [class*="curl"] pre:not(.microlight),
      [class*="curl"] code:not(.microlight)
    `.replace(/\s+/g, ' '));

    curlCodeElements.forEach((element: Element) => {
      const currentContent = element.textContent || '';
      if (this.shouldUpdateElement(currentContent)) {
        element.textContent = plainTextCurl;
        console.log('🎯 Set plain text curl in specific operation code element');
      }
    });
  }

  /**
   * Set request URL within a specific container
   */
  private setRequestUrlInContainer(
    originalUrl: string,
    proxyUrl: string,
    container: Element
  ): void {
    console.log('🎯 Setting request URL within specific operation container:', originalUrl);

    const urlElements = container.querySelectorAll('.request-url pre');
    let urlUpdated = false;

    urlElements.forEach((element: Element) => {
      const currentText = element.textContent || '';

      if (currentText.includes('proxy?target=') ||
          currentText.includes(proxyUrl) ||
          currentText.includes('{{') ||
          !currentText.trim()) {
        element.textContent = originalUrl;
        urlUpdated = true;
        console.log('🎯 Updated request URL element in specific operation');
      }
    });

    console.log(`🎯 Operation-specific URL update: ${urlUpdated ? 'updated' : 'not updated'}, checked ${urlElements.length} elements`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Check if element content should be updated
   */
  private shouldUpdateElement(content: string): boolean {
    return !content.trim() ||
           content.includes('curl') ||
           content.includes('{{');
  }

  /**
   * Apply microlight styling to element
   */
  private applyMicrolightStyling(element: HTMLElement): void {
    if (element.style) {
      element.style.fontFamily = 'monospace';
      element.style.whiteSpace = 'pre-wrap';
      element.style.wordBreak = 'break-all';
    }
  }

  /**
   * Convert curl parts to plain text
   */
  curlPartsToPlainText(curlParts: CurlPart[]): string {
    return curlParts.map(part => part.content).join('');
  }

  /**
   * Convert curl parts to HTML with syntax highlighting
   */
  curlPartsToHighlightedHtml(curlParts: CurlPart[]): string {
    return curlParts.map(part => {
      if (part.type === 'linebreak') {
        return `<span class="line-break">${this.escapeHtml(part.content)}</span>`;
      }
      return `<span style="color: ${part.color};">${this.escapeHtml(part.content)}</span>`;
    }).join('');
  }

  /**
   * Escape HTML entities
   */
  escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Attempt curl injection (used by retry logic)
   */
  private attemptCurlInjection(request: SwaggerRequest, proxyUrl: string): Observable<boolean> {
    return new Observable(observer => {
      try {
        this.injectOriginalRequestInfo(request, proxyUrl);
        observer.next(true);
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  // ============================================================================
  // Cleanup
  // ============================================================================

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
